import { BaseFeature } from '../../core/base-feature.js';

/**
 * AIVoiceFeature - Text-to-Speech with ElevenLabs (Frontend)
 * Handles: character selection, text generation, audio preview, configuration,
 * speech queue, transcript log, scheduled announcements, playback state.
 *
 * Lifecycle:
 * - initialize(): Called during registration (no DOM)
 * - onActivate(): Called when AI Voice view mounts
 * - onDeactivate(): Called when AI Voice view unmounts
 */
export class AIVoiceFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // Character data
        this.characters = [];

        // Configuration
        this.config = {
            hasApiKey: false,
            apiKeyPreview: null,
            selectedCharacterId: 'lily'
        };

        // Status
        this.status = {
            ttsReady: false,
            hasApiKey: false,
            isInitializing: false,
            initError: null,
            audioAvailable: false,
            usage: null
        };

        // Generation state
        this.isGenerating = false;
        this.isPlaying = false;

        // Current audio for preview
        this.currentAudio = null;  // HTML Audio element for preview
        this.currentCacheId = null;

        // Volume state
        this.volumes = {
            system: null,  // { left, right, average } from desktop
            tts: 80        // TTS-specific volume (0-100)
        };

        // Track last sent volumes to avoid spamming desktop
        this._lastSentVolumes = { tts: null, system: null };

        // Chunked audio transfer state
        this.pendingTransfers = new Map(); // transferId -> { metadata, chunks: [], receivedCount }

        // Playback state from desktop
        this.playbackState = { playing: false, characterId: null, characterName: null, text: null, duration: 0, progress: 0 };

        // Queue state
        this.queue = [];
        this.currentlyPlaying = null;
        this.isQueueProcessing = false;

        // Transcript (loaded from desktop)
        this.transcriptItems = [];

        // Schedules
        this.schedules = [];
    }

    static getMetadata() {
        return {
            id: 'ai-voice',
            name: 'AI Voice',
            version: 'v2.0',
            capabilities: [
                'get_characters',
                'generate',
                'speak',
                'preview',
                'stop',
                'config',
                'queue',
                'transcript',
                'schedules'
            ],
            dependencies: []
        };
    }

    /**
     * Lightweight initialization - NO DOM access
     * Called during feature registration
     */
    async initialize() {
        console.log('[AIVoice] Registered (ElevenLabs TTS)');
    }

    /**
     * Activate feature - Initialize when view mounts
     */
    async onActivate() {
        console.log('[AIVoice] Activating...');

        // Request initial data from desktop
        this.requestCharacters();
        this.requestStatus();
        this.requestConfig();
        this.requestVolumes();
        this.requestTranscript();
        this.requestQueue();
        this.requestSchedules();

        console.log('[AIVoice] Activated');
    }

    /**
     * Deactivate feature - Clean up when view unmounts
     */
    async onDeactivate() {
        console.log('[AIVoice] Deactivating...');

        // Stop any playing audio
        this.stopPreview();

        console.log('[AIVoice] Deactivated');
    }

    /**
     * Handle incoming messages from desktop
     */
    async handleMessage(message) {
        const { action, payload } = message;

        switch (action) {
            case 'get_characters_response':
                return this.handleCharactersResponse(payload);

            case 'get_status_response':
                return this.handleStatusResponse(payload);

            case 'get_config_response':
                return this.handleConfigResponse(payload);

            case 'update_config_response':
                return this.handleUpdateConfigResponse(payload);

            case 'validate_api_key_response':
                return this.handleValidateApiKeyResponse(payload);

            case 'initialize_response':
                return this.handleInitializeResponse(payload);

            case 'generate_response':
                return this.handleGenerateResponse(payload);

            case 'speak_response':
                return this.handleSpeakResponse(payload);

            case 'status_update':
                return this.handleStatusUpdate(payload);

            case 'get_volumes_response':
                return this.handleVolumesResponse(payload);

            case 'set_tts_volume_response':
                return this.handleSetTTSVolumeResponse(payload);

            case 'set_system_volume_response':
                return this.handleSetSystemVolumeResponse(payload);

            case 'audio_chunk':
                return this.handleAudioChunk(payload);

            // Playback state from desktop (via Sacred Timeline ephemeral events)
            case 'playback_state':
                return this.handlePlaybackState(payload);

            // Queue
            case 'enqueue_response':
            case 'dequeue_response':
            case 'reorder_queue_response':
                return this.handleQueueActionResponse(payload);
            case 'get_queue_response':
                return this.handleGetQueueResponse(payload);
            case 'queue_update':
                return this.handleQueueUpdate(payload);

            // Transcript
            case 'get_transcript_response':
                return this.handleGetTranscriptResponse(payload);
            case 'transcript_entry':
                return this.handleTranscriptEntry(payload);

            // Schedules
            case 'create_schedule_response':
            case 'update_schedule_response':
            case 'delete_schedule_response':
                return this.handleScheduleActionResponse(payload);
            case 'get_schedules_response':
                return this.handleGetSchedulesResponse(payload);

            default:
                console.warn(`[AIVoice] Unknown action: ${action}`);
        }
    }

    // =========================================================================
    // Public API for React component
    // =========================================================================

    requestCharacters() {
        this.sendMessage('get_characters', {});
    }

    requestStatus() {
        this.sendMessage('get_status', {});
    }

    requestConfig() {
        this.sendMessage('get_config', {});
    }

    async updateConfig(newConfig) {
        this.sendMessage('update_config', newConfig);
        return { pending: true };
    }

    async validateApiKey(apiKey) {
        this.sendMessage('validate_api_key', { apiKey });
        return { pending: true };
    }

    requestUsage() {
        this.sendMessage('get_usage', {});
    }

    requestVolumes() {
        this.sendMessage('get_volumes', {});
    }

    setTTSVolume(level) {
        this.volumes.tts = level;
        if (this.currentAudio) {
            this.currentAudio.volume = level / 100;
        }
        if (this._lastSentVolumes.tts !== level) {
            this._lastSentVolumes.tts = level;
            this.sendMessage('set_tts_volume', { level });
        }
    }

    setSystemVolume(level) {
        if (this._lastSentVolumes.system !== level) {
            this._lastSentVolumes.system = level;
            this.sendMessage('set_system_volume', { level });
        }
    }

    getVolumes() {
        return this.volumes;
    }

    async generate(text, characterId) {
        if (this.isGenerating) {
            throw new Error('Generation already in progress');
        }
        this.isGenerating = true;
        this.emit('generatingChange', true);
        try {
            this.sendMessage('generate', { text, characterId, returnAudio: true });
            return { pending: true };
        } catch (error) {
            this.isGenerating = false;
            this.emit('generatingChange', false);
            throw error;
        }
    }

    async speak(text, characterId) {
        if (this.isGenerating) {
            throw new Error('Generation already in progress');
        }
        this.isGenerating = true;
        this.emit('generatingChange', true);
        try {
            this.sendMessage('speak', { text, characterId });
        } catch (error) {
            this.isGenerating = false;
            this.emit('generatingChange', false);
            throw error;
        }
    }

    playCached(cacheId) {
        this.sendMessage('play_cached', { cacheId });
    }

    stop() {
        this.sendMessage('stop', {});
        this.stopPreview();
    }

    playPreview(audioData, mimeType = 'audio/mpeg') {
        this.stopPreview();
        try {
            const audioBlob = this._base64ToBlob(audioData, mimeType);
            const audioUrl = URL.createObjectURL(audioBlob);
            this.currentAudio = new Audio(audioUrl);
            this.currentAudio.volume = this.volumes.tts / 100;

            this.currentAudio.onplay = () => {
                this.isPlaying = true;
                this.emit('playingChange', true);
            };
            this.currentAudio.onended = () => {
                this.isPlaying = false;
                this.emit('playingChange', false);
            };
            this.currentAudio.onerror = (e) => {
                console.error('[AIVoice] Audio playback error:', e);
                this.isPlaying = false;
                this.emit('playingChange', false);
                this.emit('error', { message: 'Audio playback failed' });
            };

            this.currentAudio.play().catch(error => {
                console.error('[AIVoice] Failed to play audio:', error);
                this.emit('error', { message: 'Failed to play audio: ' + error.message });
            });
        } catch (error) {
            console.error('[AIVoice] Failed to create audio preview:', error);
            this.emit('error', { message: 'Failed to create audio preview' });
        }
    }

    stopPreview() {
        if (this.currentAudio) {
            this.currentAudio.pause();
            this.currentAudio.currentTime = 0;
            if (this.currentAudio.src) {
                URL.revokeObjectURL(this.currentAudio.src);
            }
            this.currentAudio = null;
        }
        this.isPlaying = false;
        this.emit('playingChange', false);
    }

    getCharacters() {
        return this.characters;
    }

    getStatus() {
        return this.status;
    }

    getConfig() {
        return this.config;
    }

    // Queue API
    enqueueMessage(text, characterId, priority = 'normal') {
        this.sendMessage('enqueue', { text, characterId, priority });
    }

    dequeueMessage(itemId) {
        this.sendMessage('dequeue', { itemId });
    }

    reorderQueue(orderedIds) {
        this.sendMessage('reorder_queue', { orderedIds });
    }

    requestQueue() {
        this.sendMessage('get_queue', {});
    }

    // Transcript API
    requestTranscript(limit = 50, offset = 0) {
        this.sendMessage('get_transcript', { limit, offset });
    }

    // Schedule API
    createSchedule(config) {
        this.sendMessage('create_schedule', config);
    }

    deleteSchedule(id) {
        this.sendMessage('delete_schedule', { id });
    }

    updateSchedule(id, changes) {
        this.sendMessage('update_schedule', { id, changes });
    }

    requestSchedules() {
        this.sendMessage('get_schedules', {});
    }

    // =========================================================================
    // Response handlers
    // =========================================================================

    handleCharactersResponse(payload) {
        if (payload.characters) {
            this.characters = payload.characters;
            this.emit('charactersChanged', this.characters);
            console.log('[AIVoice] Received characters:', this.characters.length);
        }
    }

    handleStatusResponse(payload) {
        this.status = {
            ttsReady: payload.ttsReady || false,
            hasApiKey: payload.hasApiKey || false,
            isInitializing: payload.isInitializing || false,
            initError: payload.initError || null,
            audioAvailable: payload.audioAvailable || false,
            usage: payload.usage || null,
            cacheSize: payload.cacheSize || 0,
            isSpeaking: payload.isSpeaking || false,
            queueLength: payload.queueLength || 0
        };
        this.emit('statusChanged', this.status);
        console.log('[AIVoice] Status:', this.status);
    }

    handleConfigResponse(payload) {
        this.config = {
            hasApiKey: payload.hasApiKey || false,
            apiKeyPreview: payload.apiKeyPreview || null,
            selectedCharacterId: payload.selectedCharacterId || 'lily'
        };
        this.emit('configChanged', this.config);
        console.log('[AIVoice] Config loaded:', this.config.hasApiKey ? 'API key set' : 'No API key');
    }

    handleUpdateConfigResponse(payload) {
        if (payload.success) {
            this.emit('configUpdated', payload);
            this.requestConfig();
            this.requestStatus();
            this.requestCharacters();
        } else {
            this.emit('error', { message: payload.error || 'Config update failed' });
        }
    }

    handleValidateApiKeyResponse(payload) {
        this.emit('apiKeyValidated', payload);
    }

    handleInitializeResponse(payload) {
        if (payload.success) {
            this.status.ttsReady = true;
            this.status.isInitializing = false;
            this.status.initError = null;
            this.emit('statusChanged', this.status);
            console.log('[AIVoice] TTS initialized successfully');
        } else {
            this.status.isInitializing = false;
            this.status.initError = payload.error || 'Initialization failed';
            this.emit('statusChanged', this.status);
            this.emit('error', { message: payload.error || 'Initialization failed' });
            console.error('[AIVoice] TTS initialization failed:', payload.error);
        }
    }

    handleGenerateResponse(payload) {
        if (payload.success) {
            this.currentCacheId = payload.cacheId;

            if (payload.chunked) {
                console.log(`[AIVoice] Starting chunked transfer: ${payload.transferId} (${payload.totalChunks} chunks, ${payload.totalSize} bytes)`);
                this.pendingTransfers.set(payload.transferId, {
                    metadata: payload,
                    chunks: new Array(payload.totalChunks),
                    receivedCount: 0,
                    type: 'generate'
                });
                return;
            }

            this.isGenerating = false;
            this.emit('generatingChange', false);

            this.emit('generated', {
                cacheId: payload.cacheId,
                audioData: payload.audioData,
                mimeType: payload.mimeType,
                duration: payload.duration
            });

            if (payload.audioData) {
                this.playPreview(payload.audioData, payload.mimeType);
            }
        } else {
            this.isGenerating = false;
            this.emit('generatingChange', false);
            this.emit('error', { message: payload.error || 'Generation failed' });
        }
    }

    handleSpeakResponse(payload) {
        if (payload.success) {
            if (payload.chunked) {
                console.log(`[AIVoice] Starting chunked transfer: ${payload.transferId} (${payload.totalChunks} chunks, ${payload.totalSize} bytes)`);
                this.pendingTransfers.set(payload.transferId, {
                    metadata: payload,
                    chunks: new Array(payload.totalChunks),
                    receivedCount: 0,
                    type: 'speak'
                });
                return;
            }

            this.isGenerating = false;
            this.emit('generatingChange', false);

            if (payload.audioData) {
                this.playPreview(payload.audioData, payload.mimeType);
            }

            this.emit('speaking', {
                cacheId: payload.cacheId,
                duration: payload.duration
            });
        } else {
            this.isGenerating = false;
            this.emit('generatingChange', false);
            this.emit('error', { message: payload.error || 'Speak failed' });
        }
    }

    handleStatusUpdate(payload) {
        Object.assign(this.status, payload);
        this.emit('statusChanged', this.status);
    }

    handleVolumesResponse(payload) {
        this.volumes = {
            system: payload.system || null,
            tts: payload.tts ?? 80
        };
        this.emit('volumesChanged', this.volumes);
        console.log('[AIVoice] Volumes:', this.volumes);
    }

    handleSetTTSVolumeResponse(payload) {
        if (payload.success) {
            this.volumes.tts = payload.tts;
            this.emit('volumesChanged', this.volumes);
        } else {
            this.emit('error', { message: payload.error || 'Failed to set TTS volume' });
        }
    }

    handleSetSystemVolumeResponse(payload) {
        if (payload.success && payload.system) {
            this.volumes.system = payload.system;
            this.emit('volumesChanged', this.volumes);
        } else if (!payload.success) {
            this.emit('error', { message: payload.error || 'Failed to set system volume' });
        }
    }

    handleAudioChunk(payload) {
        const { transferId, chunkIndex, totalChunks, data } = payload;

        const transfer = this.pendingTransfers.get(transferId);
        if (!transfer) {
            console.warn(`[AIVoice] Received chunk for unknown transfer: ${transferId}`);
            return;
        }

        transfer.chunks[chunkIndex] = data;
        transfer.receivedCount++;

        console.log(`[AIVoice] Received chunk ${chunkIndex + 1}/${totalChunks} for transfer ${transferId}`);

        if (transfer.receivedCount === totalChunks) {
            console.log(`[AIVoice] All ${totalChunks} chunks received, reassembling...`);

            const audioData = transfer.chunks.join('');
            this.pendingTransfers.delete(transferId);

            this.isGenerating = false;
            this.emit('generatingChange', false);

            const { metadata, type } = transfer;

            if (type === 'generate') {
                this.currentCacheId = metadata.cacheId;
                this.emit('generated', {
                    cacheId: metadata.cacheId,
                    audioData,
                    mimeType: metadata.mimeType,
                    duration: metadata.duration
                });
            } else if (type === 'speak') {
                this.emit('speaking', {
                    cacheId: metadata.cacheId,
                    duration: metadata.duration
                });
            }

            this.playPreview(audioData, metadata.mimeType);
            console.log(`[AIVoice] Chunked transfer complete: ${audioData.length} bytes`);
        }
    }

    // Playback state
    handlePlaybackState(payload) {
        this.playbackState = {
            playing: payload.playing || false,
            characterId: payload.characterId || null,
            characterName: payload.characterName || null,
            text: payload.text || null,
            duration: payload.duration || 0,
            progress: payload.progress || 0
        };
        this.emit('playbackStateChanged', this.playbackState);
    }

    // Queue handlers
    handleQueueActionResponse(payload) {
        if (payload.success) {
            this.requestQueue(); // Refresh queue
        } else {
            this.emit('error', { message: payload.error || 'Queue action failed' });
        }
    }

    handleGetQueueResponse(payload) {
        this.queue = payload.queue || [];
        this.currentlyPlaying = payload.currentlyPlaying || null;
        this.isQueueProcessing = payload.isProcessing || false;
        this.emit('queueChanged', {
            queue: this.queue,
            currentlyPlaying: this.currentlyPlaying,
            isProcessing: this.isQueueProcessing
        });
    }

    handleQueueUpdate(payload) {
        this.queue = payload.queue || [];
        this.currentlyPlaying = payload.currentlyPlaying || null;
        this.isQueueProcessing = payload.isProcessing || false;
        this.emit('queueChanged', {
            queue: this.queue,
            currentlyPlaying: this.currentlyPlaying,
            isProcessing: this.isQueueProcessing
        });
    }

    // Transcript handlers
    handleGetTranscriptResponse(payload) {
        this.transcriptItems = payload.items || [];
        this.emit('transcriptLoaded', {
            items: this.transcriptItems,
            total: payload.total || 0
        });
    }

    handleTranscriptEntry(payload) {
        // Real-time new transcript entry from desktop
        this.transcriptItems.unshift(payload);
        if (this.transcriptItems.length > 200) {
            this.transcriptItems = this.transcriptItems.slice(0, 200);
        }
        this.emit('transcriptUpdated', {
            entry: payload,
            items: this.transcriptItems
        });
    }

    // Schedule handlers
    handleScheduleActionResponse(payload) {
        if (payload.success) {
            this.requestSchedules(); // Refresh
        } else {
            this.emit('error', { message: payload.error || 'Schedule action failed' });
        }
    }

    handleGetSchedulesResponse(payload) {
        this.schedules = Array.isArray(payload) ? payload : [];
        this.emit('schedulesChanged', this.schedules);
    }

    // =========================================================================
    // Utility methods
    // =========================================================================

    _base64ToBlob(base64, mimeType) {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType });
    }

    async cleanup() {
        console.log('[AIVoice] Cleaning up...');
        this.stopPreview();
        this.characters = [];
        this._eventListeners.clear();
    }
}
